import visitasPlanService from './visitasPlanService';
import visitasAgendaService, { DEFAULT_AGENDA_NOMBRE } from './visitasAgendaService';
import {
  buildCreatePlanItemsPayload,
  eachIsoDayInRange,
  PlanVisitRowDraft,
} from '../pages/ventas/components/planFormHelpers';
import { PlanVisitCreateDraft, VisitasDto } from '../types/visitas';
import { VisitasAgendaAutocompletarPreview } from '../types/visitasAgenda';

const visitaExistsInPlan = (
  visitas: VisitasDto[] | null | undefined,
  nroCliente: number,
  codigoDireccion?: string,
): boolean => {
  const cod = (codigoDireccion ?? '').trim();
  return (visitas ?? []).some(
    (v) =>
      v.nroCliente === nroCliente &&
      String(v.codigoDireccion ?? '').trim() === cod,
  );
};

const toPlanVisitRows = (drafts: PlanVisitCreateDraft[]): PlanVisitRowDraft[] =>
  drafts.map((d) => ({
    item: {
      nroCliente: d.nroCliente,
      codigoDireccion: d.codigoDireccion,
      fechaHoraProgramada: null,
      observaciones: d.observaciones,
    },
    horarioPreferido: d.horarioPreferido,
  }));

const addManualVisitsToRange = async (
  nroVendedor: number,
  fechaDesde: string,
  fechaHasta: string,
  manualDrafts: PlanVisitCreateDraft[],
  observaciones?: string | null,
): Promise<{ visitasAgregadas: number; planesCreados: number; diasOmitidos: string[] }> => {
  if (manualDrafts.length === 0) {
    return { visitasAgregadas: 0, planesCreados: 0, diasOmitidos: [] };
  }

  const days = eachIsoDayInRange(fechaDesde, fechaHasta);
  const manualRows = toPlanVisitRows(manualDrafts);
  let visitasAgregadas = 0;
  let planesCreados = 0;
  const diasOmitidos: string[] = [];

  for (const day of days) {
    const items = buildCreatePlanItemsPayload(day, manualRows);
    if (items.length === 0) continue;

    try {
      const planes = await visitasPlanService.getPlanes({
        fechaDesde: day,
        fechaHasta: day,
        nroVendedor,
        pageNumber: 1,
        pageSize: 5,
      });

      const existing = planes.items[0];
      if (existing) {
        const full = await visitasPlanService.getPlan(existing.idVisitaPlan);
        for (const item of items) {
          if (
            visitaExistsInPlan(
              full.visitas,
              Number(item.nroCliente),
              item.codigoDireccion ?? undefined,
            )
          ) {
            continue;
          }
          await visitasPlanService.addVisitaToPlan(existing.idVisitaPlan, {
            nroCliente: item.nroCliente,
            codigoDireccion: item.codigoDireccion,
            fechaHoraProgramada: item.fechaHoraProgramada,
            observaciones: item.observaciones,
          });
          visitasAgregadas += 1;
        }
      } else {
        await visitasPlanService.createPlan({
          fecha: day,
          nroVendedor,
          observaciones: observaciones ?? null,
          items,
        });
        planesCreados += 1;
        visitasAgregadas += items.length;
      }
    } catch (error: unknown) {
      const err = error as { response?: { status?: number } };
      if (err?.response?.status === 409) {
        diasOmitidos.push(day);
        continue;
      }
      throw error;
    }
  }

  return { visitasAgregadas, planesCreados, diasOmitidos };
};

export const previewAutocompletarPlanes = async (
  _agendaId: string,
  nroVendedor: number,
  fechaDesde: string,
  fechaHasta: string,
): Promise<VisitasAgendaAutocompletarPreview | null> => {
  const result = await visitasAgendaService.generarPlanes({
    nroVendedor,
    fechaDesde,
    fechaHasta,
    nombreAgenda: DEFAULT_AGENDA_NOMBRE,
    modo: 'preview',
  });

  return {
    totalDias: result.totalDias,
    totalVisitasNuevas: result.totalVisitasNuevas,
    porDia: result.porDia.map((d) => ({
      fecha: d.fecha,
      visitasNuevas: d.visitasNuevas,
      planExistente: d.planExistente,
      planNuevo: d.planNuevo,
      conflicto409: d.conflicto409,
    })),
  };
};

export interface ApplyAutocompletarResult {
  planesCreados: number;
  visitasAgregadas: number;
  diasOmitidosPorConflicto: string[];
  error?: string;
}

export const applyAutocompletarPlanes = async (
  _agendaId: string,
  nroVendedor: number,
  fechaDesde: string,
  fechaHasta: string,
  manualDrafts: PlanVisitCreateDraft[] = [],
  observaciones?: string | null,
): Promise<ApplyAutocompletarResult> => {
  try {
    const result = await visitasAgendaService.generarPlanes({
      nroVendedor,
      fechaDesde,
      fechaHasta,
      nombreAgenda: DEFAULT_AGENDA_NOMBRE,
      modo: 'ejecutar',
      observaciones,
    });

    let visitasAgregadas = result.visitasAgregadas;
    let planesCreados = result.planesCreados;
    const diasOmitidosPorConflicto = [...result.diasOmitidosPorConflicto];

    if (manualDrafts.length > 0) {
      const manual = await addManualVisitsToRange(
        nroVendedor,
        fechaDesde,
        fechaHasta,
        manualDrafts,
        observaciones,
      );
      visitasAgregadas += manual.visitasAgregadas;
      planesCreados += manual.planesCreados;
      manual.diasOmitidos.forEach((d) => {
        if (!diasOmitidosPorConflicto.includes(d)) diasOmitidosPorConflicto.push(d);
      });
    }

    return { planesCreados, visitasAgregadas, diasOmitidosPorConflicto };
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } } };
    return {
      planesCreados: 0,
      visitasAgregadas: 0,
      diasOmitidosPorConflicto: [],
      error: err?.response?.data?.message || 'Error al generar planes desde la agenda.',
    };
  }
};
